import * as React from 'react';

import { cn } from '../../../utils/helpers';
import { ErrorTextProps, FormHelperText, HelperTextProps } from '../../form-helper-text/form-helper-text';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  name?: string;
  errorProps?: ErrorTextProps;
  helperProps?: HelperTextProps;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', label, errorProps, helperProps, required, name, ...props }, ref) => {
    const hasError = errorProps?.visible;

    return (
      <div className="flex flex-col space-y-2">
        {label && (
          <label
            htmlFor={name}
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}
        <input
          id={name}
          type={type}
          name={name}
          className={cn(
            // base shadcn styles - keeping most original except file classes
            'flex h-10 w-full rounded-md border bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            // dsl focus style - border outline directly on field, no spacing, midnight-900 color
            'focus:border-midnight-900 focus:ring-midnight-900',
            // conditional border state
            {
              'border-red-500': hasError,
              'border-input': !hasError,
            },
            className
          )}
          ref={ref}
          {...props}
        />

        {helperProps?.display && (
          <FormHelperText type="helper" display={helperProps.display} visible={helperProps.visible} size="large" />
        )}

        {errorProps?.display && (
          <FormHelperText type="error" display={errorProps.display} visible={errorProps.visible} size="large" />
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
